import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { motion } from "framer-motion";
import RefreshIcon from "@mui/icons-material/Refresh";
import HomeIcon from "@mui/icons-material/Home";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { BRAND_COLOR, BRAND_HOVER } from "@kivunova/kivufrontendcommon";

const CONTENT_BG = "#f8f7fc";

interface GlobalErrorFallbackProps {
  error: unknown;
  onRetry: () => void;
}

const GlobalErrorFallback: React.FC<GlobalErrorFallbackProps> = ({
  error,
  onRetry
}) => {
  const message = error instanceof Error ? error.message : String(error);
  const isDev = process.env.NODE_ENV === "development";

  const handleGoHome = () => {
    onRetry();
    window.location.href = "/";
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(160deg, ${CONTENT_BG} 0%, ${CONTENT_BG} 50%, ${BRAND_COLOR}08 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        boxSizing: "border-box"
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          duration: 0.5,
          ease: [0.25, 0.46, 0.45, 0.94]
        }}
        style={{ width: "100%", maxWidth: 480 }}
      >
        <Box
          sx={{
            background: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(12px)",
            borderRadius: 4,
            boxShadow: `0 24px 48px rgba(111, 73, 201, 0.12), 0 8px 24px rgba(0,0,0,0.06)`,
            border: "1px solid rgba(111, 73, 201, 0.12)",
            p: 4,
            textAlign: "center"
          }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 18,
              delay: 0.1
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${BRAND_COLOR} 0%, ${BRAND_HOVER} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
                boxShadow: `0 12px 32px rgba(111, 73, 201, 0.35)`
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 44, color: "white" }} />
            </Box>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#1a1a2e",
                letterSpacing: "-0.02em",
                mb: 1
              }}
            >
              Something went wrong
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                maxWidth: 360,
                mx: "auto",
                lineHeight: 1.6,
                mb: 3
              }}
            >
              An unexpected error occurred. We’ve been notified. Please try
              again or return to the home page.
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.4 }}
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              flexWrap: "wrap"
            }}
          >
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={onRetry}
              sx={{
                bgcolor: BRAND_COLOR,
                color: "white",
                fontWeight: 600,
                px: 2.5,
                py: 1.25,
                borderRadius: 2,
                textTransform: "none",
                boxShadow: `0 4px 14px rgba(111, 73, 201, 0.4)`,
                "&:hover": {
                  bgcolor: BRAND_HOVER,
                  boxShadow: `0 6px 20px rgba(111, 73, 201, 0.45)`
                }
              }}
            >
              Try again
            </Button>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={handleGoHome}
              sx={{
                borderColor: BRAND_COLOR,
                color: BRAND_COLOR,
                fontWeight: 600,
                px: 2.5,
                py: 1.25,
                borderRadius: 2,
                textTransform: "none",
                "&:hover": {
                  borderColor: BRAND_HOVER,
                  color: BRAND_HOVER,
                  bgcolor: "rgba(111, 73, 201, 0.06)"
                }
              }}
            >
              Go home
            </Button>
          </motion.div>

          {isDev && message && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: "grey.100",
                  borderRadius: 2,
                  textAlign: "left"
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    display: "block",
                    mb: 0.5
                  }}
                >
                  Development: error details
                </Typography>
                <Typography
                  component="pre"
                  variant="caption"
                  sx={{
                    fontSize: "0.7rem",
                    overflow: "auto",
                    maxHeight: 120,
                    color: "text.secondary",
                    fontFamily: "monospace"
                  }}
                >
                  {message}
                </Typography>
              </Box>
            </motion.div>
          )}
        </Box>
      </motion.div>

      {/* Decorative orbs */}
      <motion.div
        style={{
          position: "fixed",
          top: "10%",
          left: "15%",
          width: 280,
          height: 280,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${BRAND_COLOR}18 0%, transparent 70%)`,
          pointerEvents: "none"
        }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, duration: 0.6 }}
      />
      <motion.div
        style={{
          position: "fixed",
          bottom: "15%",
          right: "10%",
          width: 200,
          height: 200,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${BRAND_HOVER}12 0%, transparent 70%)`,
          pointerEvents: "none"
        }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.4, duration: 0.6 }}
      />
    </Box>
  );
};

export default GlobalErrorFallback;
